import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAddress } from '../contexts/AddressContext';
import { 
  getCurrentLanguage, 
  SupportedLanguage, 
  LANGUAGE_CHANGE_EVENT
} from '../config/languageConfig';
import useTranslation from '../hooks/useTranslation';

const LocationBanner: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { address } = useAddress();
  const [currentLang, setCurrentLang] = useState<SupportedLanguage>(getCurrentLanguage());
  
  // 앱 전체 언어 변경 시 상태 업데이트
  useEffect(() => {
    const updateLangState = () => {
      setCurrentLang(getCurrentLanguage());
    };
    
    // 언어 변경 감지를 위한 이벤트 리스너
    window.addEventListener(LANGUAGE_CHANGE_EVENT, updateLangState);
    
    return () => {
      window.removeEventListener(LANGUAGE_CHANGE_EVENT, updateLangState);
    };
  }, []);

  const handleAddressClick = () => {
    // 주소 설정 페이지로 이동
    navigate('/address');
  };

  // 주소에서 핵심 지역명 추출 함수
  const extractLocationName = (fullAddress: string, language: SupportedLanguage): string => {
    if (!fullAddress) return '';
    
    const address = fullAddress.trim();
    
    switch (language) {
      case 'en':
        // 영어: Seoul, Republic of Korea 제외
        const enParts = address.split(',').map(part => part.trim());
        const filteredParts = enParts.filter(part => 
          part !== 'Seoul' && 
          part !== 'Republic of Korea' &&
          part !== ''
        );
        return filteredParts.join(', ');
      
      case 'ko':
      case 'ja':
      case 'zh-CN':
      case 'zh-TW':
      default:
        // 한국어 및 기타 언어는 그대로 표시
        return address;
    }
  };

  return (
    <div className="px-2.5 pb-1.5 pt-2 md-background">
      {/* 위치 정보 */}
      <div 
        className="cursor-pointer py-1 flex items-center hover:bg-gray-50 rounded transition-colors"
        onClick={handleAddressClick}
      >
        <div className="flex items-center flex-grow min-w-0 pl-2">
          <div className="flex-grow min-w-0">
            <span className="md-body-small md-on-surface block truncate font-large font-bold">
              {address && address.isComplete && (address.stay?.display_info || address.mainAddress) ? 
                (() => {
                  // 표시할 주소 결정: display_info 우선, 없으면 mainAddress
                  const displayAddress = address.stay?.display_info || address.mainAddress;
                  return extractLocationName(displayAddress, currentLang) || 
                    (displayAddress.length > 30 ? 
                      `${displayAddress.substring(0, 30)}...` : 
                      displayAddress
                    );
                })() : 
                t('header.setAddress')
              }
            </span>
          </div>
        </div>
        
        {/* 아래쪽 화살표 아이콘 */}
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 md-on-surface-variant flex-shrink-0">
          <path d="M7 10l5 5 5-5z" />
        </svg>
      </div>
    </div>
  );
};

export default LocationBanner; 